/**
 * 開新分頁時的 session 交接（一次性、短效）。
 *
 * 為什麼需要這個東西：轉派彈窗的箭頭是 `target="_blank"`，而「記住我」沒勾時
 * session 住在 **sessionStorage** —— sessionStorage 是**每個分頁一份**的。
 * 瀏覽器雖然會在開新分頁時複製一份給它，但**只在新分頁保有 opener 的時候**；
 * 加了 `rel="noopener"`（`noreferrer` 也隱含 noopener）就不複製，新分頁一開
 * 就是空的 → `hydrate()` 找不到身分 → 被踢回登入頁。
 *
 * 所以不要靠瀏覽器那個隱性行為，改成**明著交接**：開之前把身分放進 localStorage
 * 的交接槽，新分頁 `hydrate()` 讀出來寫進**自己的** sessionStorage。
 * 交接槽有 30 秒有效期，過期的一律不認 —— 它是傳遞用的，不是第二個 session 家。
 *
 * 「記住我」有勾（session 在 localStorage）時根本不會用到這裡：
 * localStorage 本來就跨分頁共用，新分頁自己就 hydrate 得起來。
 */

const HANDOFF_KEY = "authHandoff";

/** 交接槽存活時間。開新分頁到跑完 hydrate 是毫秒級，30 秒已經很寬鬆。 */
export const HANDOFF_TTL_MS = 30 * 1000;

/**
 * 把身分放進交接槽。回傳有沒有真的放進去（storage 不可用時回 false，呼叫端不必處理）。
 * 只在「session 不住 localStorage」時才有意義，判斷交給呼叫端（sessionStore）。
 */
export function stashHandoff(user, token, now = Date.now()) {
  if (!user || !token) return false;
  try {
    localStorage.setItem(
      HANDOFF_KEY,
      JSON.stringify({ user, token, expires_at: now + HANDOFF_TTL_MS })
    );
    return true;
  } catch (e) {
    return false; // SSR／隱私模式
  }
}

/**
 * 讀交接槽。過期或格式壞掉一律回 null **並就地清掉**（不留殘渣）；
 * 還在有效期內的**不刪** —— 一次點開兩三個箭頭是很正常的事，
 * 讀一次就作廢會讓第二個分頁莫名其妙被踢回登入頁。時效由 `expires_at` 管，
 * 登出時由 `clearHandoff()` 收乾淨。
 */
export function readHandoff(now = Date.now()) {
  let raw = null;
  try {
    raw = localStorage.getItem(HANDOFF_KEY);
  } catch (e) {
    return null;
  }
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    const usable =
      data && data.user && data.token
      && typeof data.expires_at === "number" && data.expires_at > now;
    if (!usable) {
      clearHandoff();
      return null;
    }
    return { user: data.user, token: data.token };
  } catch (e) {
    clearHandoff();
    return null;
  }
}

/** 登出／換人登入時一併清掉,避免舊身分被下一個分頁撿走 */
export function clearHandoff() {
  try {
    localStorage.removeItem(HANDOFF_KEY);
  } catch (e) {
    /* ignore */
  }
}
